import type { QueryClient } from "@tanstack/react-query";
import { State, type Subscription, SubscriptionState } from "centrifuge";

import type { ConnectionState } from "./ConnectionState";
import { createCentrifuge } from "./createCentrifuge";
import { fetchRealtimeToken } from "./fetchRealtimeToken";
import { parseRealtimeEvent } from "./parseRealtimeEvent";
import type { RealtimeHandlerRegistry } from "./RealtimeHandlerRegistry";

type ConnectRealtimeOptions = {
  queryClient: QueryClient;
  registry: RealtimeHandlerRegistry;
  onStateChange: (state: ConnectionState) => void;
};

// Одно соединение с Centrifugo и подписка на канал пользователя.
//
// Канал (`user:#<id>`) приходит вместе с токеном, поэтому подписка создаётся
// после первого `getToken`. Токен запрашивает сам SDK: при подключении и до
// истечения срока; сетевые ошибки он повторяет с растущей задержкой.
//
// «Подключено» — это соединение есть и подписка активна: только тогда события
// доходят, и экраны могут не опрашивать API.
//
// Возвращает функцию отключения.
export const connectRealtime = ({
  queryClient,
  registry,
  onStateChange,
}: ConnectRealtimeOptions): (() => void) => {
  const handlerContext = { queryClient };
  let subscription: Subscription | null = null;
  let disposed = false;
  let lastState: ConnectionState | null = null;

  const emitState = () => {
    if (disposed) {
      return;
    }
    let state: ConnectionState;
    if (centrifuge.state === State.Connected) {
      state = subscription?.state === SubscriptionState.Subscribed ? "connected" : "connecting";
    } else if (centrifuge.state === State.Connecting) {
      state = "connecting";
    } else {
      state = "disconnected";
    }
    if (state !== lastState) {
      lastState = state;
      onStateChange(state);
    }
  };

  const subscribe = (channel: string) => {
    if (subscription?.channel === channel) {
      return;
    }
    // Канал сменился (в другой вкладке вошли под другим пользователем) — переподписка.
    if (subscription !== null) {
      subscription.removeAllListeners();
      subscription.unsubscribe();
      centrifuge.removeSubscription(subscription);
    }
    const next = centrifuge.newSubscription(channel);
    next.on("publication", (ctx) => {
      const event = parseRealtimeEvent(ctx.data);
      if (event === null) {
        console.warn("realtime: unknown event", ctx.data);
        return;
      }
      registry.dispatch(event, handlerContext);
    });
    // Пропущенное не восстановить — перечитываем через REST все данные пользователя.
    next.on("subscribed", (ctx) => {
      if (ctx.wasRecovering && !ctx.recovered) {
        void queryClient.invalidateQueries();
      }
    });
    next.on("state", emitState);
    subscription = next;
    next.subscribe();
  };

  const getToken = async (): Promise<string> => {
    const { token, channel } = await fetchRealtimeToken();
    if (!disposed) {
      subscribe(channel);
    }
    return token;
  };

  const centrifuge = createCentrifuge(getToken);
  centrifuge.on("state", emitState);
  emitState();
  centrifuge.connect();

  return () => {
    disposed = true;
    centrifuge.removeAllListeners();
    subscription?.removeAllListeners();
    centrifuge.disconnect();
  };
};
